import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { isEndBeforeStart } from '../state';
import type { DetailsPatch, DetailsValues } from '../state';
import { projectDuration } from '../state/detailsView';
import { formatRange } from '../state/formatDates';

import { FieldHelp, FormField } from './FormField';
import { ResourcePlanningFields } from './ResourcePlanningFields';
import { SettingsDateField } from './SettingsDateField';

import { MaterialSymbol } from '@/components/MaterialSymbol';

/**
 * When, edited (brief §3): start and end, then an optional delivery date that
 * can be cleared. An end before the start warns and never blocks (decision 4):
 * Save stays enabled and both dates are kept as entered. Then Resource
 * planning, moved here from Where it belongs (decision 18).
 */
export type WhenFormProps = {
  draft: Pick<
    DetailsValues,
    'startDate' | 'endDate' | 'deliveryDate' | 'includeInResourcePlanning' | 'likelihoodPct'
  >;
  onChange: (patch: DetailsPatch) => void;
};

export const WhenForm = ({ draft, onChange }: WhenFormProps) => {
  const { t } = useTranslation('pmDetails');
  const { startDate, endDate, deliveryDate } = draft;
  const endFirst = isEndBeforeStart(startDate, endDate);
  const duration = projectDuration(startDate, endDate);

  const rangeBelow = endFirst ? (
    <Stack
      direction="row"
      role="status"
      sx={{ alignItems: 'center', gap: 0.5 }}
      data-automation-id="details-when-end-before-start"
    >
      <MaterialSymbol name="warning" size={14} color="var(--color-warning-80)" />
      <Typography variant="label" sx={{ color: 'var(--color-warning-100)' }}>
        {t('when.EndBeforeStart')}
      </Typography>
    </Stack>
  ) : duration ? (
    <FieldHelp>
      {formatRange(startDate, endDate)} ·{' '}
      {t(duration.unit === 'days' ? 'when.Days' : 'when.Months', { count: duration.count })}
    </FieldHelp>
  ) : null;

  return (
    <Stack sx={{ gap: 2 }}>
      <FormField label={t('when.StartEnd')} below={rangeBelow}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <SettingsDateField
            size="field"
            value={startDate}
            placeholder={t('when.StartDate')}
            onCommit={(iso) => onChange({ startDate: iso })}
            automationId="details-when-start"
          />
          <Typography component="span" sx={{ color: 'var(--color-fog-80)' }}>
            –
          </Typography>
          <SettingsDateField
            size="field"
            value={endDate}
            placeholder={t('when.EndDate')}
            onCommit={(iso) => onChange({ endDate: iso })}
            automationId="details-when-end"
          />
        </Stack>
      </FormField>
      <FormField label={t('when.DeliveryOptional')}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <SettingsDateField
            size="field"
            value={deliveryDate}
            placeholder={t('when.NoDelivery')}
            onCommit={(iso) => onChange({ deliveryDate: iso })}
            automationId="details-when-delivery-field"
          />
          {deliveryDate !== '' ? (
            <Tooltip title={t('when.ClearDelivery')}>
              <IconButton
                size="small"
                aria-label={t('when.ClearDelivery')}
                onClick={() => onChange({ deliveryDate: '' })}
                data-automation-id="details-when-clear-delivery"
              >
                <MaterialSymbol name="close" size={20} />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      </FormField>
      <ResourcePlanningFields draft={draft} onChange={onChange} />
    </Stack>
  );
};
